import type { Polygon } from 'geojson';
import type { MapMouseEvent } from 'mapbox-gl';
import { type ReactElement, useEffect, useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { calculateArea } from '~/helpers/unitHelpers';
import { criticalInfraGeoJson as cnpCriticalInfraGeoJson } from './cnpCriticalInfrastructure';

const CRITICAL_INFRA_ID = 'cnpCriticalInfra';

export default function CriticalInfraPolygons({
  setPopup,
}: {
  setPopup: (props: { lngLat: { lng: number; lat: number }; content: ReactElement }) => void;
}) {
  const { current: map } = MapGL.useMap();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    function onPolygonClick(e: MapMouseEvent) {
      const { features, lngLat } = e;
      if (features?.[0]) {
        const f = features[0];
        const a = calculateArea({
          unitSystem: 'imperial',
          unitType: 'acres',
          geometry: f.geometry as Polygon,
        });

        setPopup({
          lngLat,
          content: (
            <div className="min-w-[200px] space-y-1 p-2">
              <div className="mb-2 font-mono text-2xs uppercase text-secondary">CNP Critical Infrastructure</div>
              <div className="text-2xs font-semibold uppercase">Project</div>
              <div>{f.properties!.Name}</div>
              <div>
                {a.value.toLocaleString()} {a.unit}
              </div>
            </div>
          ),
        });
      }
    }

    map?.on('click', 'critical-infra-polygon', onPolygonClick);

    return () => {
      map?.off('click', 'critical-infra-polygon', onPolygonClick);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const features = useMemo(() => {
    return (
      <MapGL.Source id={CRITICAL_INFRA_ID} key={CRITICAL_INFRA_ID} type="geojson" data={cnpCriticalInfraGeoJson}>
        <MapGL.Layer
          source={CRITICAL_INFRA_ID}
          type="fill"
          id={`critical-infra-polygon`}
          paint={{
            'fill-color': 'red',
            'fill-opacity': 0.25,
          }}
        />

        <MapGL.Layer
          source={CRITICAL_INFRA_ID}
          type="line"
          id={`line-layer-${CRITICAL_INFRA_ID}`}
          paint={{
            'line-width': 2,
            'line-color': 'red',
          }}
        />
      </MapGL.Source>
    );
  }, [cnpCriticalInfraGeoJson]);

  return <>{features}</>;
}
